import React, { useEffect, useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  TextInput,
  Alert,
  ActivityIndicator,
} from "react-native";
import { useLocalSearchParams, router } from "expo-router";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  query,
  type DocumentData,
  type DocumentSnapshot,
  where,
} from "firebase/firestore";

import { checkCurrentUserIsAdmin } from "@/lib/admin/adminAuth";
import { auth, db } from "@/lib/firebase";
import type { Business } from "@/lib/types";
import {
  adminApprovePublish,
  adminReject,
  adminRequestChanges,
  adminSoftDelete,
  adminToggleFeatured,
  adminUnpublish,
  normalizeAdminBusiness,
  type AdminBusiness,
} from "@/lib/admin/businessesAdmin";

type AdminAction =
  | "approve"
  | "unpublish"
  | "featured"
  | "changes"
  | "reject"
  | "delete";

type AdminDebugAction = AdminAction | "snapshot" | "resolve";

type ResolvedBusiness = {
  id: string;
  snap: DocumentSnapshot<DocumentData>;
  source: "document_id" | "embedded_businessId" | "businessId_field" | "slug";
};

type AdminBusinessDetailData = Business & AdminBusiness;

function cleanString(value: unknown) {
  return typeof value === "string" ? value.trim() : "";
}

function businessFromSnapshot(snap: DocumentSnapshot<DocumentData>) {
  const data = snap.data() as Record<string, unknown> | undefined;

  return snap.exists()
    ? (normalizeAdminBusiness(data ?? {}, snap.id) as AdminBusinessDetailData)
    : null;
}

function formatAdminError(error: unknown) {
  const maybeError = error as { code?: unknown; message?: unknown };
  const message =
    typeof maybeError?.message === "string" && maybeError.message.trim()
      ? maybeError.message
      : "Something went wrong";
  const code =
    typeof maybeError?.code === "string" && maybeError.code.trim()
      ? maybeError.code
      : "";

  return code ? `${message} (${code})` : message;
}

function logAdminDetailDebug(
  event: string,
  details: Record<string, unknown>
) {
  if (__DEV__) {
    void details;
    console.log("[AdminBusinessDetail]", event);
  }
}

function logAdminActionError(action: AdminDebugAction, error: unknown) {
  if (__DEV__) {
    const maybeError = error as { code?: unknown };
    const code = String(maybeError?.code ?? "").trim().slice(0, 80);
    console.error(
      `Admin business ${action} failed.`,
      code ? { code } : undefined
    );
  }
}

async function readBusinessDocument(businessId: string) {
  return getDoc(doc(db, "businesses", businessId));
}

async function resolveBusinessByRouteParam(
  routeIdParam: string
): Promise<ResolvedBusiness | null> {
  const routeId = cleanString(routeIdParam);

  if (!routeId) return null;

  const directSnap = await readBusinessDocument(routeId);

  if (directSnap.exists()) {
    const directData = directSnap.data() as Record<string, unknown>;
    const embeddedBusinessId = cleanString(directData.businessId);

    if (embeddedBusinessId && embeddedBusinessId !== directSnap.id) {
      const embeddedSnap = await readBusinessDocument(embeddedBusinessId);

      if (embeddedSnap.exists()) {
        return {
          id: embeddedSnap.id,
          snap: embeddedSnap,
          source: "embedded_businessId",
        };
      }
    }

    return {
      id: directSnap.id,
      snap: directSnap,
      source: "document_id",
    };
  }

  const businessIdSnap = await getDocs(
    query(
      collection(db, "businesses"),
      where("businessId", "==", routeId),
      limit(1)
    )
  );

  const businessIdDoc = businessIdSnap.docs[0];

  if (businessIdDoc) {
    return {
      id: businessIdDoc.id,
      snap: businessIdDoc,
      source: "businessId_field",
    };
  }

  const slugSnap = await getDocs(
    query(collection(db, "businesses"), where("slug", "==", routeId), limit(1))
  );

  const slugDoc = slugSnap.docs[0];

  if (slugDoc) {
    return {
      id: slugDoc.id,
      snap: slugDoc,
      source: "slug",
    };
  }

  return null;
}

export default function AdminBusinessDetail() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const routeIdParam = typeof id === "string" ? id : "";

  const [loading, setLoading] = useState(true);
  const [biz, setBiz] = useState<AdminBusinessDetailData | null>(null);
  const [resolvedBusinessId, setResolvedBusinessId] = useState("");
  const [resolveSource, setResolveSource] =
    useState<ResolvedBusiness["source"] | null>(null);
  const [busyAction, setBusyAction] = useState<AdminAction | null>(null);

  const [rejectReason, setRejectReason] = useState("");
  const [changesMsg, setChangesMsg] = useState("");

  useEffect(() => {
    let cancelled = false;
    let unsubscribe: (() => void) | undefined;

    async function resolveAndListen() {
      if (!routeIdParam) {
        setBiz(null);
        setResolvedBusinessId("");
        setResolveSource(null);
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        const resolved = await resolveBusinessByRouteParam(routeIdParam);

        if (cancelled) return;

        logAdminDetailDebug("route_resolved", {
          routeIdParam,
          resolvedBusinessId: resolved?.id ?? null,
          resolveSource: resolved?.source ?? null,
          loadedBusinessId: resolved?.snap.id ?? null,
          loadedBusinessBusinessId: resolved?.snap.exists()
            ? cleanString(resolved.snap.data()?.businessId)
            : null,
          authUid: auth.currentUser?.uid ?? null,
        });

        if (!resolved) {
          setBiz(null);
          setResolvedBusinessId("");
          setResolveSource(null);
          setLoading(false);
          return;
        }

        setResolvedBusinessId(resolved.id);
        setResolveSource(resolved.source);
        setBiz(businessFromSnapshot(resolved.snap));
        setLoading(false);

        const ref = doc(db, "businesses", resolved.id);
        unsubscribe = onSnapshot(
          ref,
          (snap) => {
            setBiz(businessFromSnapshot(snap));
            setResolvedBusinessId(snap.id);
            setLoading(false);
          },
          (error) => {
            logAdminActionError("snapshot", error);
            setBiz(null);
            setLoading(false);
          }
        );
      } catch (error) {
        if (cancelled) return;

        logAdminActionError("resolve", error);
        setBiz(null);
        setResolvedBusinessId("");
        setResolveSource(null);
        setLoading(false);
      }
    }

    void resolveAndListen();

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [routeIdParam]);

  const refreshBusiness = async () => {
    if (!resolvedBusinessId) return;

    const snap = await readBusinessDocument(resolvedBusinessId);
    setBiz(businessFromSnapshot(snap));
  };

  const getCurrentBusinessDocumentId = () =>
    cleanString(resolvedBusinessId) ||
    cleanString(biz?.id) ||
    cleanString(routeIdParam);

  const getFinalBusinessDocumentId = () => {
    const finalDocumentId = getCurrentBusinessDocumentId();

    if (!finalDocumentId) {
      throw new Error("Missing business document id.");
    }

    return finalDocumentId;
  };

  const buildActionDebugDetails = (
    action: AdminAction,
    finalDocumentId?: string,
    adminConfirmed?: boolean | null
  ) => ({
    action,
    routeIdParam,
    loadedBusinessId: biz?.id ?? null,
    loadedBusinessBusinessId: cleanString((biz as any)?.businessId) || null,
    finalDocumentId: finalDocumentId ?? null,
    authUid: auth.currentUser?.uid ?? null,
    adminRoleConfirmed: adminConfirmed ?? null,
    resolveSource,
  });

  const logButtonPressed = (action: AdminAction) => {
    logAdminDetailDebug(
      "button_pressed",
      buildActionDebugDetails(action, getCurrentBusinessDocumentId(), null)
    );
  };

  const runAdminAction = async (
    action: AdminAction,
    successMessage: string,
    fn: (finalDocumentId: string) => Promise<void>,
    afterSuccess?: () => void
  ) => {
    let finalDocumentId: string | undefined;
    let adminRoleConfirmed: boolean | null = null;

    setBusyAction(action);

    try {
      finalDocumentId = getFinalBusinessDocumentId();
      const adminCheck = await checkCurrentUserIsAdmin();
      adminRoleConfirmed = adminCheck.isAdmin;

      logAdminDetailDebug("before_update", {
        ...buildActionDebugDetails(
          action,
          finalDocumentId,
          adminRoleConfirmed
        ),
        adminRole: adminCheck.role ?? null,
        adminCheckReason: adminCheck.reason ?? null,
      });

      if (!adminCheck.isAdmin) {
        throw new Error(
          adminCheck.reason ?? "Current user is not confirmed as admin."
        );
      }

      await fn(finalDocumentId);
      await refreshBusiness();

      logAdminDetailDebug(
        "after_update_success",
        buildActionDebugDetails(action, finalDocumentId, adminRoleConfirmed)
      );

      Alert.alert("Success", successMessage);
      afterSuccess?.();
    } catch (error) {
      const maybeError = error as { code?: unknown; message?: unknown };

      logAdminDetailDebug("update_error", {
        ...buildActionDebugDetails(
          action,
          finalDocumentId,
          adminRoleConfirmed
        ),
        errorCode: maybeError?.code ?? null,
        errorMessage: maybeError?.message ?? null,
      });
      logAdminActionError(action, error);
      Alert.alert("Error", formatAdminError(error));
    } finally {
      setBusyAction(null);
    }
  };

  const confirm = (
    title: string,
    msg: string,
    action: AdminAction,
    successMessage: string,
    fn: (finalDocumentId: string) => Promise<void>,
    afterSuccess?: () => void
  ) => {
    logButtonPressed(action);

    Alert.alert(title, msg, [
      { text: "Cancel", style: "cancel" },
      {
        text: "OK",
        style: "destructive",
        onPress: () =>
          void runAdminAction(action, successMessage, fn, afterSuccess),
      },
    ]);
  };

  if (loading) {
    return (
      <View style={[styles.container, styles.center]}>
        <ActivityIndicator />
      </View>
    );
  }

  if (!biz) {
    return (
      <View style={styles.container}>
        <Text style={styles.title}>Business not found</Text>
        <Pressable style={styles.btn} onPress={() => router.back()}>
          <Text style={styles.btnText}>Back</Text>
        </Pressable>
      </View>
    );
  }

  const isFeatured = biz.isFeatured === true;
  const featuredLabel = isFeatured ? "Yes" : "No";
  const rankLabel =
    typeof biz.featuredRank === "number" ? String(biz.featuredRank) : "—";
  const categoryLabel = cleanString(biz.displayCategory) || "Not set";
  const subcategoryLabel = cleanString(biz.displaySubcategory) || "Not set";

  return (
    <View style={styles.container}>
      <Text style={styles.title}>{biz.name || "Unnamed business"}</Text>
      <Text style={styles.slug}>/{biz.slug || "no-slug"}</Text>

      {/* NEW: open full customer-style card preview */}
      <Pressable
        style={[styles.btn, { marginTop: 12 }]}
        onPress={() =>
  router.push({
    pathname: "/admin/moderation/full/[id]",
    params: { id: biz.id! },
  })
}
      >
        <Text style={styles.btnText}>Open Full Card Preview</Text>
      </Pressable>

      <View style={styles.metaBox}>
        <Text style={styles.meta}>Status: {biz.status ?? "unknown"}</Text>
        {/* If your DB uses isPublished, it may not be in Business type yet;
            keep as any-safe read if needed */}
        <Text style={styles.meta}>
          Published: {(biz as any).isPublished ? "Yes" : "No"}
        </Text>
        <Text style={styles.meta}>
          Deleted: {(biz as any).isDeleted ? "Yes" : "No"}
        </Text>

        <View style={styles.metaRow}>
          <Text style={styles.meta}>Category: {categoryLabel}</Text>
          {biz.isCategoryOwnerSuggested ? (
            <View style={styles.ownerSuggestedBadge}>
              <Text style={styles.ownerSuggestedText}>Owner suggested</Text>
            </View>
          ) : null}
        </View>

        <View style={styles.metaRow}>
          <Text style={styles.meta}>Subcategory: {subcategoryLabel}</Text>
          {biz.isSubcategoryOwnerSuggested ? (
            <View style={styles.ownerSuggestedBadge}>
              <Text style={styles.ownerSuggestedText}>Owner suggested</Text>
            </View>
          ) : null}
        </View>

        <Text style={styles.meta}>Featured: {featuredLabel}</Text>
        <Text style={styles.meta}>Featured rank: {rankLabel}</Text>
      </View>

      <View style={styles.actions}>
        <Pressable
          style={[styles.btn, busyAction === "approve" && styles.disabledBtn]}
          disabled={!!busyAction}
          onPress={() =>
            confirm(
              "Approve & Publish",
              "Make this business public?",
              "approve",
              "Business approved and published.",
              async (finalDocumentId) => {
                await adminApprovePublish(finalDocumentId);
              }
            )
          }
        >
          <Text style={styles.btnText}>
            {busyAction === "approve" ? "Approving..." : "Approve + Publish"}
          </Text>
        </Pressable>

        <Pressable
          style={[styles.btn, busyAction === "unpublish" && styles.disabledBtn]}
          disabled={!!busyAction}
          onPress={() => {
            logButtonPressed("unpublish");
            void runAdminAction(
              "unpublish",
              "Business unpublished.",
              async (finalDocumentId) => {
                await adminUnpublish(finalDocumentId);
              }
            );
          }}
        >
          <Text style={styles.btnText}>
            {busyAction === "unpublish" ? "Unpublishing..." : "Unpublish"}
          </Text>
        </Pressable>

        <Pressable
          style={[styles.btn, busyAction === "featured" && styles.disabledBtn]}
          disabled={!!busyAction}
          onPress={() => {
            logButtonPressed("featured");
            void runAdminAction(
              "featured",
              isFeatured
                ? "Business removed from featured list."
                : "Business marked as featured.",
              async (finalDocumentId) => {
                await adminToggleFeatured(finalDocumentId, !isFeatured);
              }
            );
          }}
        >
          <Text style={styles.btnText}>
            {busyAction === "featured"
              ? "Saving..."
              : isFeatured
                ? "Remove Featured"
                : "Mark as Featured"}
          </Text>
        </Pressable>

        {/* NEW: request changes box */}
        <View style={styles.box}>
          <Text style={styles.boxTitle}>Request changes (send to owner)</Text>
          <TextInput
            value={changesMsg}
            onChangeText={setChangesMsg}
            placeholder="Tell the owner what to fix…"
            style={styles.input}
            multiline
          />
          <Pressable
            style={[styles.btn, busyAction === "changes" && styles.disabledBtn]}
            disabled={!!busyAction}
            onPress={() => {
              const msg = changesMsg.trim();

              if (!msg) {
                Alert.alert("Message required", "Please write a short note.");
                return;
              }

              confirm(
                "Request changes",
                "Send change request to owner?",
                "changes",
                "Change request sent to owner.",
                async (finalDocumentId) => {
                  await adminRequestChanges(finalDocumentId, msg);
                  setChangesMsg("");
                }
              );
            }}
          >
            <Text style={styles.btnText}>
              {busyAction === "changes" ? "Sending..." : "Request changes"}
            </Text>
          </Pressable>
        </View>

        {/* Reject */}
        <View style={[styles.box, { borderColor: "#f3c5c5" }]}>
          <Text style={styles.boxTitle}>Reject reason (optional)</Text>
          <TextInput
            value={rejectReason}
            onChangeText={setRejectReason}
            placeholder="Why rejected?"
            style={styles.input}
            multiline
          />
          <Pressable
            style={[
              styles.btn,
              { borderColor: "#f3c5c5" },
              busyAction === "reject" && styles.disabledBtn,
            ]}
            disabled={!!busyAction}
            onPress={() =>
              confirm(
                "Reject",
                "Reject this business?",
                "reject",
                "Business rejected.",
                async (finalDocumentId) => {
                  await adminReject(finalDocumentId, rejectReason);
                  setRejectReason("");
                }
              )
            }
          >
            <Text style={styles.btnText}>
              {busyAction === "reject" ? "Rejecting..." : "Reject"}
            </Text>
          </Pressable>
        </View>

        <Pressable
          style={[
            styles.btn,
            { borderColor: "#ddd" },
            busyAction === "delete" && styles.disabledBtn,
          ]}
          disabled={!!busyAction}
          onPress={() =>
            confirm(
              (biz as any).isDeleted ? "Restore" : "Soft delete",
              (biz as any).isDeleted ? "Restore this business?" : "Mark as deleted?",
              "delete",
              (biz as any).isDeleted
                ? "Business restored."
                : "Business marked as deleted.",
              async (finalDocumentId) => {
                await adminSoftDelete(finalDocumentId, !(biz as any).isDeleted);
              },
              () => router.back()
            )
          }
        >
          <Text style={styles.btnText}>
            {busyAction === "delete"
              ? "Saving..."
              : (biz as any).isDeleted
                ? "Restore"
                : "Soft Delete"}
          </Text>
        </Pressable>
      </View>

      <Pressable
        style={[styles.btn, { marginTop: 14 }]}
        onPress={() => router.back()}
      >
        <Text style={styles.btnText}>← Back</Text>
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, padding: 16, backgroundColor: "#fff" },
  center: { alignItems: "center", justifyContent: "center" },
  title: { fontSize: 20, fontWeight: "900" },
  slug: { marginTop: 4, opacity: 0.65 },

  metaBox: {
    marginTop: 14,
    borderWidth: 1,
    borderColor: "#eee",
    borderRadius: 16,
    padding: 12,
    gap: 6,
  },
  meta: { fontWeight: "800", opacity: 0.8 },
  metaRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 6,
  },
  ownerSuggestedBadge: {
    borderWidth: 1,
    borderColor: "#D97706",
    backgroundColor: "#FFFBEB",
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  ownerSuggestedText: {
    color: "#92400E",
    fontSize: 10,
    fontWeight: "900",
    textTransform: "uppercase",
  },

  actions: { marginTop: 14, gap: 10 },

  btn: {
    borderWidth: 1,
    borderColor: "#eee",
    borderRadius: 16,
    paddingVertical: 12,
    paddingHorizontal: 14,
  },
  btnText: { fontWeight: "900" },
  disabledBtn: { opacity: 0.55 },

  box: {
    borderWidth: 1,
    borderColor: "#eee",
    borderRadius: 16,
    padding: 12,
    gap: 10,
  },
  boxTitle: { fontWeight: "900" },

  input: {
    borderWidth: 1,
    borderColor: "#eee",
    borderRadius: 14,
    paddingHorizontal: 12,
    paddingVertical: 10,
    minHeight: 44,
  },
});
